/**
 * The words and tones for a case's Sigma "Quality of Complaints" clocks
 * (Jess Finlaw, QT-10056; kanban 6c648cab). The server computes every
 * reading (services/investigations/sla.py → DoorInvestigationRead.clocks);
 * this only phrases it: "due Mon, Oct 19", "3d late", "met Oct 16",
 * "watching · 166d left", and which tone a chip takes.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type {
  DoorCaseTaskRead,
  DoorInvestigationRead,
  DoorSlaClockRead,
  SlaClockKey,
  SlaClockStatus,
  StageRecordBody,
} from '../../services/qcpInvestigations';

/** The four Sigma stages, in the order Jess listed them. */
export const SIGMA_STAGES: ReadonlyArray<{ stage: number; label: string }> = [
  { stage: 1, label: 'The issue' },
  { stage: 2, label: 'The solution' },
  { stage: 3, label: 'Effectiveness' },
  { stage: 4, label: 'Customer response' },
];

export type ClockTone = 'ok' | 'warn' | 'bad' | 'muted' | 'info';

export function clockTone(status: SlaClockStatus): ClockTone {
  switch (status) {
    case 'met':
    case 'held':
      return 'ok';
    case 'due_soon':
      return 'warn';
    case 'overdue':
    case 'missed':
    case 'failed':
      return 'bad';
    case 'watching':
    case 'on_track':
      return 'info';
    case 'inactive':
      return 'muted';
  }
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function weekdayDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** The chip's status words: what the clock says about itself right now. */
export function clockStatusLine(clock: DoorSlaClockRead): string {
  const due = clock.due_at ? weekdayDate(clock.due_at) : null;
  const projected = clock.projected ? ' (projected)' : '';
  switch (clock.status) {
    case 'met':
      return clock.done_at ? `met ${shortDate(clock.done_at)}` : 'met';
    case 'missed':
      return clock.done_at && due
        ? `done ${shortDate(clock.done_at)}, was due ${due}`
        : 'missed';
    case 'overdue': {
      const late = clock.days_remaining === null ? null : -clock.days_remaining;
      return late !== null && late > 0 ? `${late}d late${projected}` : `overdue${projected}`;
    }
    case 'due_soon':
      return due ? `due ${due}${projected}` : `due soon${projected}`;
    case 'on_track':
      return due ? `due ${due}${projected}` : `on track${projected}`;
    case 'watching': {
      const days = clock.days_remaining !== null ? `${clock.days_remaining}d left` : null;
      const invoices = invoiceProgress(clock);
      const parts = [days, invoices].filter((p): p is string => p !== null);
      return parts.length ? `watching · ${parts.join(' · ')}` : 'watching';
    }
    case 'held':
      return 'held — no repeat in the window';
    case 'failed':
      return 'failed — the issue came back';
    case 'inactive':
      return clock.stage === 3 ? 'after completion' : 'not running';
  }
}

/** "2 of 10 invoices" on the stage-3 watch, or null when the server does not
 *  count them (older server, or a case with no customer + product). */
export function invoiceProgress(
  clock: Pick<DoorSlaClockRead, 'invoices_seen' | 'invoices_target'>,
): string | null {
  if (clock.invoices_seen === null || clock.invoices_seen === undefined) return null;
  if (!clock.invoices_target) return null;
  return `${clock.invoices_seen} of ${clock.invoices_target} invoices`;
}

/** The clocks a case carries, in stage order; [] for an older server. */
export function caseClocks(inv: Pick<DoorInvestigationRead, 'clocks'>): DoorSlaClockRead[] {
  return inv.clocks ?? [];
}

/** The stage-3 effectiveness clock, when the case carries one. */
export function effectivenessClock(
  inv: Pick<DoorInvestigationRead, 'clocks'>,
): DoorSlaClockRead | null {
  return caseClocks(inv).find((c) => c.clock === 'effectiveness') ?? null;
}

/** The clocks running late right now (not the ones already judged missed). */
export function overdueClocks(inv: Pick<DoorInvestigationRead, 'clocks'>): DoorSlaClockRead[] {
  return caseClocks(inv).filter((c) => c.status === 'overdue');
}

/** Open checklist items whose committed date has passed. */
export function overdueTasks(inv: Pick<DoorInvestigationRead, 'tasks'>): DoorCaseTaskRead[] {
  return inv.tasks.filter((t) => t.status === 'open' && t.overdue === true);
}

/** Is anything on this case past its date — a Sigma clock or a dated item? */
export function isOverdueCase(
  inv: Pick<DoorInvestigationRead, 'clocks' | 'tasks' | 'overdue_clock_count'>,
): boolean {
  const clocks = inv.overdue_clock_count ?? overdueClocks(inv).length;
  return clocks > 0 || overdueTasks(inv).length > 0;
}

/** "⏰ 2 clocks overdue · 1 item past its date" for a card, or null. */
export function overdueLine(
  inv: Pick<DoorInvestigationRead, 'clocks' | 'tasks' | 'overdue_clock_count'>,
): string | null {
  const clocks = inv.overdue_clock_count ?? overdueClocks(inv).length;
  const items = overdueTasks(inv).length;
  if (!clocks && !items) return null;
  const parts: string[] = [];
  if (clocks) parts.push(`${clocks} clock${clocks === 1 ? '' : 's'} overdue`);
  if (items) parts.push(`${items} item${items === 1 ? '' : 's'} past ${items === 1 ? 'its' : 'their'} date`);
  return `⏰ ${parts.join(' · ')}`;
}

/** The checklist row's owner-and-date words: "Jake Butchko · due Oct 28". */
export function taskAssignmentLine(task: DoorCaseTaskRead): string | null {
  const parts: string[] = [];
  if (task.owner_name) parts.push(task.owner_name);
  if (task.due_at) parts.push(`${task.overdue ? 'was due' : 'due'} ${shortDate(`${task.due_at}T12:00:00`)}`);
  return parts.length ? parts.join(' · ') : null;
}

/** "Closed from chat by Merlin Oct 21: Rich asked them for samples 10/14" on
 *  a ticked item the user settled by telling Merlin (kanban 101a9853); null
 *  for any other item. */
export function taskClosedFromChatLine(
  task: Pick<DoorCaseTaskRead, 'status' | 'done_in_chat' | 'done_at' | 'done_note' | 'done_by_label'>,
): string | null {
  if (task.status !== 'done' || !task.done_in_chat) return null;
  const who = task.done_by_label || 'Merlin';
  const when = task.done_at ? ` ${shortDate(task.done_at)}` : '';
  const note = (task.done_note ?? '').trim();
  return `Closed from chat by ${who}${when}${note ? `: ${note}` : ''}`;
}

/** "Sent Oct 16 · Emailed Dave the lot swap." for the customer-response box. */
export function customerResponseLine(
  inv: Pick<DoorInvestigationRead, 'customer_response_sent_at' | 'customer_response_summary'>,
): string | null {
  if (!inv.customer_response_sent_at) return null;
  const when = `Sent ${shortDate(inv.customer_response_sent_at)}`;
  return inv.customer_response_summary ? `${when} · ${inv.customer_response_summary}` : when;
}

/* ── The chip's detail panel (kanban 823ec064) ─────────────────────────── */

/** What the detail panel prints for one clock: the server's words, dated. */
export interface StageDetail {
  meaning: string;
  how: string;
  /** "due Mon, Oct 19 (projected)" / "was due …" / null when not running. */
  dueLine: string | null;
  /** "Done Oct 18" / "Met Oct 18" / null while running. */
  doneLine: string | null;
  /** "Marked by Jess Finlaw, Oct 18 (told to Merlin in chat)" or null. */
  recordedLine: string | null;
  /** What backs the stage on this case: findings, items, the response text. */
  evidence: string[];
  markable: boolean;
}

function dueWords(clock: DoorSlaClockRead): string | null {
  if (!clock.due_at) return null;
  const when = weekdayDate(clock.due_at);
  const projected = clock.projected && !clock.done_at ? ' (projected)' : '';
  if (clock.status === 'met' || clock.status === 'missed') return `was due ${when}`;
  if (clock.status === 'watching' || clock.status === 'held') return `watch ends ${when}`;
  if (clock.status === 'failed') return `watch was to end ${when}`;
  return `due ${when}${projected}`;
}

function doneWords(clock: DoorSlaClockRead): string | null {
  if (!clock.done_at) return null;
  if (clock.status === 'held') return `Held through ${shortDate(clock.done_at)}`;
  return `${clock.status === 'missed' ? 'Done late' : 'Done'} ${shortDate(clock.done_at)}`;
}

function recordedWords(clock: DoorSlaClockRead): string | null {
  if (!clock.recorded_by) return null;
  const when = clock.recorded_at ? `, ${shortDate(clock.recorded_at)}` : '';
  const chat = clock.recorded_in_chat ? ' (told to Merlin in chat)' : '';
  return `Last marked by ${clock.recorded_by}${when}${chat}`;
}

/** The dated checklist items (owner + date) as "[done] Swap seals — Jake · due Oct 20". */
function datedItemLines(inv: Pick<DoorInvestigationRead, 'tasks'>): string[] {
  return inv.tasks
    .filter((t) => t.owner_name && t.due_at && t.status !== 'cancelled')
    .map((t) => `${t.status === 'done' ? '✓' : '○'} ${t.label} — ${taskAssignmentLine(t) ?? ''}`);
}

function excerpt(text: string, max = 160): string {
  const one = text.replace(/\s+/g, ' ').trim();
  return one.length > max ? `${one.slice(0, max - 1).trimEnd()}…` : one;
}

/** Everything the panel says about one clock, from the server's reading and
 *  the case's own record (pure; the component only lays it out). */
export function stageDetail(
  inv: Pick<
    DoorInvestigationRead,
    | 'tasks'
    | 'findings'
    | 'title'
    | 'remediation_details'
    | 'corrective_action_summary'
    | 'corrective_action_owner'
    | 'customer_response_summary'
  >,
  clock: DoorSlaClockRead,
): StageDetail {
  const evidence: string[] = [];
  switch (clock.clock) {
    case 'issue_identified':
      evidence.push(`Case: ${inv.title}`);
      break;
    case 'root_cause':
      evidence.push(inv.findings.trim() ? `Findings: ${excerpt(inv.findings)}` : 'No findings written yet.');
      break;
    case 'corrective_action': {
      if (inv.corrective_action_summary) {
        evidence.push(
          `Corrective action: ${inv.corrective_action_summary}${
            inv.corrective_action_owner ? ` — ${inv.corrective_action_owner}` : ''
          }`,
        );
      }
      const items = datedItemLines(inv);
      if (items.length) evidence.push(...items);
      else evidence.push('No checklist item carries an owner and a date yet.');
      if (inv.remediation_details.length) {
        evidence.push(`Remediation actions: ${excerpt(inv.remediation_details.join('; '))}`);
      }
      break;
    }
    case 'customer_acknowledged':
      evidence.push(
        clock.done_at
          ? `The customer heard from us through their sales rep ${shortDate(clock.done_at)}.`
          : 'No acknowledgement recorded yet — the first word goes through the sales rep.',
      );
      break;
    case 'customer_response':
      evidence.push(
        inv.customer_response_summary
          ? `Told the customer: ${inv.customer_response_summary}`
          : 'Nothing recorded as sent to the customer yet.',
      );
      break;
    case 'effectiveness': {
      const invoices = invoiceProgress(clock);
      if (invoices) evidence.push(`Invoices of the product to the customer since completion: ${invoices}.`);
      break;
    }
  }
  return {
    meaning: clock.meaning ?? '',
    how: clock.how ?? '',
    dueLine: dueWords(clock),
    doneLine: doneWords(clock),
    recordedLine: recordedWords(clock),
    evidence,
    markable: clock.markable ?? false,
  };
}

/** Which fields the stage's edit form shows. */
export interface StageEditSpec {
  summaryLabel: string | null;
  summaryPlaceholder: string;
  hasOwner: boolean;
  /** The stamp can be removed (stage 4 is corrected, never cleared). */
  canClear: boolean;
  /** The button's verb: "Mark complete" / "Mark sent". */
  markLabel: string;
}

export function stageEditSpec(clock: SlaClockKey): StageEditSpec {
  switch (clock) {
    case 'corrective_action':
      return {
        summaryLabel: 'The corrective action',
        summaryPlaceholder: 'What we are doing about it, in a sentence or two…',
        hasOwner: true,
        canClear: true,
        markLabel: 'Mark complete',
      };
    case 'customer_response':
      return {
        summaryLabel: 'What the customer was told',
        summaryPlaceholder: 'What the customer was told, in a sentence or two…',
        hasOwner: false,
        canClear: false,
        markLabel: 'Mark sent',
      };
    case 'issue_identified':
      return { summaryLabel: null, summaryPlaceholder: '', hasOwner: false, canClear: true, markLabel: 'Mark identified' };
    case 'customer_acknowledged':
      return { summaryLabel: null, summaryPlaceholder: '', hasOwner: false, canClear: true, markLabel: 'Mark acknowledged' };
    case 'root_cause':
      return { summaryLabel: null, summaryPlaceholder: '', hasOwner: false, canClear: true, markLabel: 'Mark confirmed' };
    case 'effectiveness':
      return { summaryLabel: null, summaryPlaceholder: '', hasOwner: false, canClear: false, markLabel: '' };
  }
}

/** Is there a stamp on the row to remove? Stage 2 done through its dated
 *  checklist items or the case's completion carries none (the server would
 *  answer "Nothing changed"), so the control hides; an explicit stamp, the
 *  case-opened stamp and the transition stamp can be removed. */
export function stampRemovable(clock: DoorSlaClockRead, spec: StageEditSpec): boolean {
  if (!spec.canClear || !clock.done_at) return false;
  return clock.decided_by !== 'dated_items' && clock.decided_by !== 'case_completed';
}

/** A YYYY-MM-DD from the date input, as noon local that day in ISO — so the
 *  stamp reads as that calendar day against the 17:00 deadline (the same
 *  rule Merlin's chat stamp uses). */
export function stampAtNoonLocal(dayIso: string): string {
  const [y, m, d] = dayIso.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

/** The YYYY-MM-DD a stamp falls on, on this computer's calendar. */
export function stampDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : todayIso(d);
}

/** The body the stage form sends: a mark (with the day typed, if any), an
 *  edit of the record, or a clear. Returns null when there is nothing to send. */
export function stageRecordBody(
  clock: DoorSlaClockRead,
  spec: StageEditSpec,
  draft: { day: string; summary: string; owner: string },
  current: { summary: string | null | undefined; owner: string | null | undefined },
  action: 'mark' | 'save' | 'clear',
): StageRecordBody | null {
  if (action === 'clear') return spec.canClear ? { clear_completed: true } : null;
  const body: StageRecordBody = {};
  if (spec.summaryLabel !== null && draft.summary.trim() !== (current.summary ?? '').trim()) {
    body.summary = draft.summary.trim();
  }
  if (spec.hasOwner && draft.owner.trim() !== (current.owner ?? '').trim()) {
    body.owner_name = draft.owner.trim();
  }
  if (action === 'mark') {
    body.mark_complete = true;
    if (draft.day) body.completed_at = stampAtNoonLocal(draft.day);
  } else if (clock.done_at && draft.day && draft.day !== stampDay(clock.done_at)) {
    // Re-dating a finished stage is a mark at the new day.
    body.mark_complete = true;
    body.completed_at = stampAtNoonLocal(draft.day);
  }
  return Object.keys(body).length ? body : null;
}

/** Today as the date input wants it (YYYY-MM-DD, local calendar). */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
